import { CloudOff, Columns3, ExternalLink, Package, Plus, Trash2 } from "lucide-react";
import type { ClipboardEvent as ReactClipboardEvent, KeyboardEvent as ReactKeyboardEvent, ReactNode } from "react";
import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { parseShoppingNoteLine } from "@household/shared/shopping";
import { normalizeFoodName } from "@household/shared/mealAssembler";
import { toastLinkForEntity } from "../../../app/entityRoutes.js";
import { money } from "../../../app/helpers.js";
import type { EntityReferenceId, Location, Product } from "../../../app/types.js";
import { useHousehold } from "../../context.js";
import { onHandLabel } from "../../groceries/onHand.js";
import { ProductDefinitionDialog } from "../../groceries/ProductDefinitionDialog.js";
import { typicalPriceNote, typicalPriceOf, typicalPriceTitle } from "../../groceries/typicalPrice.js";
import { SheetCell } from "../sheet/SheetCell.js";
import { useCaretCell } from "../sheet/useCaretCell.js";
import { SheetColumnsMenu, SheetOfflineBanner, sheetSyncStatusLabel } from "../sheet/SheetChrome.js";
import { parseSheetPaste } from "../sheet/sheetKeyboard.js";
import { useSheetMenuPlacement } from "../sheet/sheetMenuPlacement.js";
import { SHEET_ACTION_COLUMN_WIDTH_WITH_STOCK, useSheetColumns, type SheetColumnDef } from "../sheet/sheetColumns.js";
import { ListSheetToolbar } from "./ListSheetToolbar.js";
import { isRetryableSyncError, LIST_SHEET_CREATE_BATCH, useListSheetSync } from "./listSheetSync.js";
import {
  emptySeed,
  isNumericColumn,
  isStampedColumn,
  minorToDecimal,
  NUMERIC_COLUMNS,
  parsePriceMinor,
  parseQuantity,
  reformatNumbers,
  seedFromPayload,
  seededOr,
  stampAll,
  type Cells,
  type ListColumnKey,
  type ListItemPayload,
  type NumericColumn,
  type SeededNumbers,
  type TypedInByColumn
} from "./listSheetCells.js";
import { formatNumberForInput, getResolvedLocale, onResolvedLocaleChange, stampForEdit, type TypedIn } from "../../../app/locale.js";

// Spreadsheet-style editor for shopping-list shaped rows. The sheet is the
// source of truth while it is open: every keystroke updates local state
// immediately (no revert flashes) and is synced to the server in the
// background, debounced and offline-aware.

export type SheetListItem = {
  id: EntityReferenceId;
  name: string;
  quantity?: number | null;
  unit: string;
  size: string;
  productUrl: string;
  expectedPriceMinor?: number | null;
  priceCurrency: string;
  storeSection: string;
  notes?: string;
  sortOrder?: number;
  productId?: EntityReferenceId | null;
};

export type { ListItemPayload, ListColumnKey } from "./listSheetCells.js";

export const standardUnits = [
  "oz", "lb", "g", "kg", "mg", "fl oz", "cup", "pt", "qt", "gal", "ml", "l", "tsp", "tbsp",
  "each", "ea", "ct", "dozen", "pair", "pack", "case", "bag", "box", "bottle", "can", "jar",
  "carton", "tube", "roll", "bunch", "loaf", "tray", "bar", "slice", "serving"
];

const unitDatalistId = "list-sheet-unit-options";
const SYNC_DEBOUNCE_MS = 450;

/**
 * Shopping's column vocabulary, expressed in the shared sheet column model
 * (#911). The TYPE is shared with Food; the COLUMNS below are not — product
 * URL and expected price are shopping-only, and Food's target level and
 * expiration are food-only. That split is the parity matrix in code form (see
 * docs/food-shopping-sheet-parity.md).
 */
export type ColumnDef = SheetColumnDef<ListColumnKey>;

const sheetColumns: ColumnDef[] = [
  { key: "name", label: "Item", labelKey: "sheet.column.name", width: "minmax(160px, 2.4fr)", always: true },
  { key: "quantity", label: "Qty", labelKey: "sheet.column.quantity", width: "68px", inputMode: "decimal" },
  { key: "unit", label: "Unit", labelKey: "sheet.column.unit", width: "88px", listId: unitDatalistId },
  { key: "size", label: "Size", labelKey: "sheet.column.size", width: "100px" },
  { key: "price", label: "Price", labelKey: "sheet.column.price", width: "92px", inputMode: "decimal" },
  { key: "link", label: "Link", labelKey: "sheet.column.link", width: "minmax(140px, 1.2fr)", inputMode: "url" },
  { key: "section", label: "Section", labelKey: "sheet.column.section", width: "112px" },
  { key: "notes", label: "Notes", labelKey: "sheet.column.notes", width: "minmax(140px, 1.6fr)" }
];

const desktopDefaultColumns: ListColumnKey[] = ["name", "quantity", "price", "link"];
const mobileDefaultColumns: ListColumnKey[] = ["name"];

// --- cell value parsing -----------------------------------------------------
//
// Numbers live in listSheetCells.ts, where they can be tested without a DOM.

function sanitizeUrl(value: string | undefined): string {
  const trimmed = (value ?? "").trim();
  if (!trimmed) return "";
  const withScheme = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
  return /^https?:\/\/\S+$/i.test(withScheme) ? withScheme.slice(0, 500) : "";
}

function safeCellUrl(value: string | undefined): string {
  const trimmed = (value ?? "").trim();
  return /^https?:\/\//i.test(trimmed) ? trimmed : "";
}

/**
 * The shared note parser reads inline detail out of a free-text line —
 * "Olive oil 2 lb $12.99". It is English-only on purpose: it lives in the shared
 * package and the API and the assistant read the same lines.
 *
 * Under any other language it is DECLINED, and the whole line is kept as the
 * name. Rewriting the numbers to suit it was tried and was worse: a separator
 * between two digits is not necessarily a number, so a Spanish user typing the
 * name "Modelo 1.234" had a different product stored. Extracting a price is a
 * convenience; getting the name wrong, or the price wrong by a hundredfold, is
 * not a fair trade for it. The price and quantity cells are right there.
 */
export function shoppingLinePayload(line: string, typedIn: TypedIn): ListItemPayload | null {
  // `typedIn`, not the current language: this decision belongs to the moment the
  // line was TYPED. Asking the app what language it is in now got it wrong for
  // any row still pending when the language changed — offline in a shop, or just
  // inside the 450 ms debounce — and handed a Spanish line to the English parser
  // (codex review).
  if (typedIn !== "en") return null;
  const parsed = parseShoppingNoteLine(line);
  if (!parsed) return null;
  return {
    name: parsed.name,
    quantity: parsed.quantity ?? null,
    unit: parsed.unit ?? "",
    size: parsed.size ?? "",
    productUrl: parsed.productUrl ?? "",
    expectedPriceMinor: parsed.expectedPriceMinor ?? null,
    priceCurrency: parsed.priceCurrency ?? "USD",
    storeSection: "",
    notes: parsed.notes ?? "",
    productId: null
  };
}

// --- row model --------------------------------------------------------------

type Row = { key: string; serverId: EntityReferenceId | null; cells: Cells; dirty: boolean; saving: boolean; productId: EntityReferenceId | null; seeded: SeededNumbers; typedIn: TypedInByColumn };
/**
 * A row the server no longer has, and where it sat, so Undo can put it back
 * (#296).
 *
 * `cleared` distinguishes the two ways a row leaves the list, because they need
 * different undos. A DELETED row is gone from the sheet, and Undo re-inserts it.
 * A CLEARED row — one whose name was emptied — is deleted server-side just the
 * same, but stays on screen as a blank, so Undo has to put the name back into
 * the row that is already there rather than add a second one.
 */
type RemovedRow = { row: Row; index: number; cleared: boolean };
export type CellPos = { row: number; col: number };

let keySeq = 0;
function nextKey(): string {
  keySeq += 1;
  return `r${keySeq}`;
}

function cellsFromItem(item: SheetListItem): Cells {
  return {
    name: item.name ?? "",
    // Seeded through the SAME formatter the parser reads back, or the round trip
    // is broken at the other end: a server value of 1.5 shown as "1.5" to a
    // Spanish session is rejected by parseQuantity, and editing any OTHER cell
    // then sends the whole row with quantity null — silently erasing a quantity
    // nobody touched.
    quantity: item.quantity == null ? "" : formatNumberForInput(item.quantity),
    unit: item.unit ?? "",
    size: item.size ?? "",
    price: minorToDecimal(item.expectedPriceMinor),
    link: item.productUrl ?? "",
    section: item.storeSection ?? "",
    notes: item.notes ?? ""
  };
}

function blankRow(): Row {
  return { key: nextKey(), serverId: null, cells: {}, dirty: false, saving: false, productId: null, seeded: emptySeed(getResolvedLocale()), typedIn: stampAll(getResolvedLocale()) };
}

function cellName(row: Row): string {
  return (row.cells.name ?? "").trim();
}

function rowIsBlank(row: Row): boolean {
  return sheetColumns.every((column) => !(row.cells[column.key] ?? "").trim());
}

function ensureTrailingBlank(rows: Row[]): Row[] {
  const last = rows[rows.length - 1];
  return !last || !rowIsBlank(last) ? [...rows, blankRow()] : rows;
}

/**
 * Put a removed row back where it was (#296).
 *
 * Exported for its own test: an Undo that lands the row in the wrong place, or
 * that lands a second copy of it, is worse than no Undo at all, and neither is
 * visible from a first-paint assertion.
 *
 * The row on screen always wins. If this row is already here — a second tap, or
 * a reseed that got there first — the list is returned untouched rather than
 * overwritten, because what is on screen may have been edited since and this
 * snapshot is by definition older than that.
 *
 * `index` is where the row was, which is not always where it can go: rows may
 * have been deleted from above it while the toast was up, and the trailing
 * blank is the "add a row" affordance and has to stay last.
 */
export function rowsWithRestored(rows: Row[], restored: Row, index: number): Row[] {
  const alreadyHere = rows.some((row) => (
    row.key === restored.key || (restored.serverId != null && row.serverId === restored.serverId)
  ));
  if (alreadyHere) return rows;
  const last = rows[rows.length - 1];
  const insertable = last && rowIsBlank(last) ? rows.length - 1 : rows.length;
  const at = Math.max(0, Math.min(index, insertable));
  return ensureTrailingBlank([...rows.slice(0, at), restored, ...rows.slice(at)]);
}

/**
 * Put the name back into a row that was cleared (#296).
 *
 * Clearing a row's name deletes the item server-side while leaving the row on
 * screen, blank — which is the quietest destructive action in this sheet, and
 * the reason it needed an undo more than the obvious one did. The other cells
 * were never destroyed, so this puts back the one thing that was: the name.
 *
 * Three states, three answers:
 *
 * - the row is still there and still blank: the name goes back into it, under
 *   the server id it just got back. `dirty` is recomputed rather than assumed
 *   false, so a quantity typed into the row during the window is still owed to
 *   the server and still gets sent;
 * - the row has been typed into since: that is a NEW item the user is creating,
 *   and it is not this one. The restored item is inserted beside it instead of
 *   overwriting what they wrote;
 * - the row is gone (deleted outright while the toast was up): the restored item
 *   is inserted where it sat, which is what the delete's own undo would do.
 *
 * `item` is the server's snapshot, not the sheet's — the sheet does not have
 * one, because the name is exactly what was erased from it.
 */
export function rowsWithClearedRestored(rows: Row[], cleared: RemovedRow, item: SheetListItem | null): Row[] {
  if (!item) return rows;
  const at = rows.findIndex((row) => row.key === cleared.row.key);
  const inPlace = at >= 0 ? rows[at] : undefined;
  if (!inPlace || inPlace.serverId != null || cellName(inPlace) !== "") {
    return rowsWithRestored(rows, seedRows([item])[0] as Row, cleared.index);
  }
  const fromServer = cellsFromItem(item);
  const cells: Cells = { ...inPlace.cells, name: fromServer.name };
  const restored: Row = {
    ...inPlace,
    serverId: item.id,
    cells,
    saving: false,
    dirty: sheetColumns.some((column) => (cells[column.key] ?? "") !== (fromServer[column.key] ?? ""))
  };
  return ensureTrailingBlank([...rows.slice(0, at), restored, ...rows.slice(at + 1)]);
}

/** The sheet's rows as they arrive from the server. Exported as a test seam:
 * `rowsWithRestored` operates on rows, and building one by hand would mean
 * copying the locale stamping this does. */
export function seedRows(items: SheetListItem[]): Row[] {
  const locale = getResolvedLocale();
  return ensureTrailingBlank(items.map((item) => {
    const cells = cellsFromItem(item);
    return {
      key: nextKey(),
      serverId: item.id,
      cells,
      dirty: false,
      saving: false,
      productId: item.productId ?? null,
      // cellsFromItem formatted these numbers a moment ago, in today's language,
      // so the row and its seed agree on the convention by construction.
      typedIn: stampAll(locale),
      seeded: {
        quantity: { text: cells.quantity ?? "", value: item.quantity ?? null, typedIn: locale },
        price: { text: cells.price ?? "", value: item.expectedPriceMinor ?? null, typedIn: locale }
      }
    };
  }));
}

function fieldsFromCells(cells: Cells, productId: EntityReferenceId | null, seeded: SeededNumbers, typedIn: TypedInByColumn): ListItemPayload {
  return {
    name: (cells.name ?? "").trim().slice(0, 200),
    quantity: seededOr(cells.quantity, seeded.quantity, typedIn.quantity, (value) => parseQuantity(value, typedIn.quantity)),
    unit: (cells.unit ?? "").trim().slice(0, 40),
    size: (cells.size ?? "").trim().slice(0, 80),
    productUrl: sanitizeUrl(cells.link),
    expectedPriceMinor: seededOr(cells.price, seeded.price, typedIn.price, (value) => parsePriceMinor(value, typedIn.price)),
    priceCurrency: "USD",
    storeSection: (cells.section ?? "").trim().slice(0, 120),
    notes: (cells.notes ?? "").trim().slice(0, 1000),
    productId
  };
}

/**
 * The payload for a row the server has not seen, and whether the NOTE PARSER
 * built it.
 *
 * That second half matters because the parser reads a line the sheet is still
 * displaying whole. "2 bags chips $5.99" is saved as name "chips", quantity 2,
 * unit "bags", price 5.99 — while the row on screen still shows the raw line in
 * Name and empty Qty, Unit and Price cells. Nothing used to reconcile them, so
 * the next edit to ANY cell in that row sent the raw line back as the name and
 * erased the unit, and the footer totalled a price whose cell was blank (codex
 * review). The caller puts the parsed fields into the cells instead.
 */
function createPayloadFromCells(
  cells: Cells,
  productId: EntityReferenceId | null,
  seeded: SeededNumbers,
  typedIn: TypedInByColumn
): { payload: ListItemPayload; fromNoteParser: boolean } | null {
  const name = (cells.name ?? "").trim();
  if (!name) return null;
  const onlyName = sheetColumns.every((column) => column.key === "name" || !(cells[column.key] ?? "").trim());
  if (onlyName && !productId) {
    // A bare name may carry inline details ("2 lb chicken $5.99") the note
    // parser understands; fall back to a plain name if it does not. Skipped
    // once a catalog definition is linked so the parser cannot silently
    // strip the link by re-deriving a plain name-only payload.
    const parsed = shoppingLinePayload(name, typedIn.name);
    if (parsed) return { payload: parsed, fromNoteParser: true };
  }
  return { payload: fieldsFromCells(cells, productId, seeded, typedIn), fromNoteParser: false };
}

/** The cells that show what a payload actually saved. */
function cellsFromPayload(payload: ListItemPayload): Cells {
  return {
    name: payload.name,
    quantity: payload.quantity == null ? "" : formatNumberForInput(payload.quantity),
    unit: payload.unit,
    size: payload.size,
    price: minorToDecimal(payload.expectedPriceMinor),
    link: payload.productUrl,
    section: payload.storeSection,
    notes: payload.notes
  };
}

/**
 * The rows this pass will create, and the payload each one sends.
 *
 * Capped at one batch: the endpoint takes 100 items, and a pass that tried to
 * send more in several requests would throw away the ids the earlier ones had
 * already been given as soon as a later one failed (codex review on #1122).
 * Whatever is left over is still dirty, so the flush loop comes straight back
 * for it — by which time this batch's rows have their ids and are no longer
 * creates.
 *
 * `retryKeys` is the batch a previous pass sent and never heard back about
 * (#1235). Given it, this returns exactly those rows, in that order, and NOT
 * whatever else has gone dirty since. That is the difference between a retry
 * and a new request: the request id is derived from the row keys, so a pass
 * that re-composed [r1,r2,r3] as [r1,r2,r3,r4] would mint a NEW id, the server
 * would read it as a first attempt, and r1-r3 — which the lost attempt may
 * already have committed — would land a second time. One keystroke during the
 * outage was enough. The new row is not dropped, only deferred: it is still
 * dirty, and `runFlush` loops until nothing syncable is left.
 */
export function createCandidates(
  rows: Row[],
  retryKeys?: readonly string[] | null
): Array<{ row: Row; payload: ListItemPayload; fromNoteParser: boolean }> {
  const eligible = rows
    .filter((row) => row.dirty && !row.saving && row.serverId == null && cellName(row) !== "")
    .map((row) => ({ row, built: createPayloadFromCells(row.cells, row.productId, row.seeded, row.typedIn) }))
    .filter((pair): pair is { row: Row; built: { payload: ListItemPayload; fromNoteParser: boolean } } => pair.built != null)
    .map((pair) => ({ row: pair.row, payload: pair.built.payload, fromNoteParser: pair.built.fromNoteParser }));

  if (retryKeys?.length) {
    const byKey = new Map(eligible.map((pair) => [pair.row.key, pair]));
    const frozen = retryKeys.map((key) => byKey.get(key));
    // All of them, in the order they were sent: the request id is derived from
    // the row keys, and the sheet maps `added` back by POSITION. If even one of
    // those rows is gone, the batch cannot be reproduced and this falls through
    // to a fresh one — a new request, which is honest about what it is.
    if (frozen.every((pair) => pair != null)) {
      return frozen as Array<{ row: Row; payload: ListItemPayload; fromNoteParser: boolean }>;
    }
  }

  return eligible.slice(0, LIST_SHEET_CREATE_BATCH);
}

/**
 * The row a completed create leaves behind.
 *
 * `createdId` is the id the server gave this row, or null when the response
 * carried none. `unchanged` is whether the row still holds the values that were
 * sent — a keystroke made while the write was in the air keeps the row dirty so
 * the follow-up pass writes it.
 *
 * Lives outside the component so the arithmetic can be tested without a DOM.
 */
export function rowAfterCreate(
  row: Row,
  sent: { payload: ListItemPayload; fromNoteParser: boolean } | undefined,
  createdId: EntityReferenceId | null,
  unchanged: boolean
): Row {
  // No id came back, so this row is still a create the server has never seen
  // (#1122). Retiring it as saved is the one thing that must not happen: with
  // `serverId` null it can never be updated or deleted, and the next edit is
  // filtered as a create and sends the whole row a SECOND time. Nothing else is
  // touched either — re-baselining cells against a payload that was not stored
  // would show the user a tidy-up the server never made.
  if (createdId == null) return { ...row, saving: false, dirty: true };
  if (!sent) return { ...row, serverId: createdId, saving: false, dirty: !unchanged };
  // What the note parser pulled out of the name goes into the cells it came
  // from, so the row shows what was actually saved. Only when the user has not
  // typed since: their keystrokes outrank a tidy-up.
  const reconciled = sent.fromNoteParser && unchanged ? cellsFromPayload(sent.payload) : null;
  const cells = reconciled ?? row.cells;
  // Those cells were formatted a moment ago, in today's language, so they are
  // stamped with it. Leaving the row's old stamps on freshly formatted text is
  // the pairing this whole seam exists to prevent (codex review).
  const typedIn = reconciled
    ? { ...row.typedIn, quantity: getResolvedLocale(), price: getResolvedLocale() }
    : row.typedIn;
  return {
    ...row,
    cells,
    typedIn,
    serverId: createdId,
    saving: false,
    dirty: !unchanged,
    // The seed renders the saved numbers itself, in today's language.
    seeded: seedFromPayload(sent.payload, getResolvedLocale())
  };
}

/**
 * A signature for the TEXT cells, used to tell whether a row changed while a
 * save was in the air.
 *
 * Numeric cells are deliberately absent: a language change rewrites their text
 * without changing what they MEAN, and including the text made that rewrite
 * look like an edit — the row was re-sent whole and could overwrite a
 * housemate's change to a field nobody here had touched (codex review). Their
 * meanings go into rowSig separately.
 *
 * The separator is spelled \u0001 rather than written as a literal control
 * character, which is invisible: two reviewers read it as an empty join and
 * reported a column-boundary collision that cannot happen.
 */
function cellsSig(cells: Cells): string {
  return sheetColumns
    .filter((column) => !isNumericColumn(column.key))
    .map((column) => cells[column.key] ?? "")
    .join("\u0001");
}

function rowSig(row: Row): string {
  // Retyping the SAME digits under a different language while the write is in
  // the air is a real change that comparing text alone would miss. But the
  // numbers go in as VALUES rather than as stamps, because what matters is
  // whether the row now MEANS something different — "7" is seven in both
  // languages, and comparing stamps made a no-op keystroke an unsaved change
  // that nothing could ever clear.
  const meaning = NUMERIC_COLUMNS
    .map((column) => (column === "price"
      ? parsePriceMinor(row.cells.price, row.typedIn.price)
      : parseQuantity(row.cells.quantity, row.typedIn.quantity)))
    .join("|");
  return `${cellsSig(row.cells)}|${row.productId ?? ""}|${meaning}|${row.typedIn.name}`;
}

function rowsTotalMinor(rows: Row[]): number {
  return Math.round(rows.reduce((total, row) => {
    // Same rule as the flush path: an untouched cell answers with the number
    // the server sent. Re-parsing its text meant that after a language change
    // every untouched row read as null and the footer showed a total of zero
    // for a list full of priced items.
    const price = seededOr(row.cells.price, row.seeded.price, row.typedIn.price, (value) => parsePriceMinor(value, row.typedIn.price));
    if (price == null) return total;
    return total + price * (seededOr(row.cells.quantity, row.seeded.quantity, row.typedIn.quantity, (value) => parseQuantity(value, row.typedIn.quantity)) ?? 1);
  }, 0));
}

// Exported for sibling views (home cards, metrics) that total server items.
export function listSheetTotalMinor(items: SheetListItem[]): number {
  return Math.round(items.reduce((total, item) => total + (item.expectedPriceMinor ?? 0) * (item.quantity ?? 1), 0));
}

// ---------------------------------------------------------------------------

export function ListSheet({
  addControl,
  currency = "USD",
  definitions,
  flushControl,
  items,
  listId,
  locations = [],
  onMarkOutOfStock,
  storageKey
}: {
  /**
   * Imperative add handle (September audit): the parent's "Add an item" field
   * hands a name to the sheet, which writes it into the trailing blank row
   * through the same path a typed name takes — so the row syncs, queues
   * offline and links to the catalog exactly as if it had been typed. The
   * field exists because nothing told a new person that items are added by
   * typing into the empty row at the bottom; the only input above the sheet
   * was the store picker, and an item name became a store.
   */
  addControl?: { current: ((name: string) => void) | null };
  currency?: string;
  /** Household product catalog (#635). When omitted the name cell stays a
   * plain text input — other ListSheet consumers (put-away, project docs)
   * are unaffected. */
  definitions?: Product[];
  /**
   * Imperative flush handle (#597): the parent stores a function that pushes
   * every pending edit and resolves true when nothing syncable remains. Used
   * before remounting the sheet (e.g. the aisle-sort toggle) so a reseed can
   * never drop debounced or offline edits.
   */
  flushControl?: { current: (() => Promise<boolean>) | null };
  items: SheetListItem[];
  listId: EntityReferenceId | null;
  /** Needed by the "Create new…" product dialog only; ignored when
   * `definitions` is omitted. */
  locations?: Location[];
  /**
   * Turns the sheet into the other half of the ambient inventory loop (#1058).
   * When supplied, a row linked to a food shows what is on hand right there,
   * and a row whose food still shows stock offers "we're out" in one tap —
   * because noticing you are out of something IS what putting it on the list
   * means, and that moment is the only one at which anybody will ever say so.
   * Restock suggestions already flow the other way onto the list; this closes
   * the loop in the same surface.
   *
   * Optional so the sheet's other consumers (project documents, BOM rows) are
   * untouched — stock is a food concept, not a spreadsheet one.
   */
  onMarkOutOfStock?: (product: Product) => void | Promise<void>;
  storageKey: string;
}) {
  const sync = useListSheetSync(listId);
  const { showToast } = useHousehold();
  const { t } = useTranslation("common");
  // Every column carries a bundle key; `label` is only the English fallback for
  // a column that has not been given one.
  const columnLabel = (column: ColumnDef): string => (column.labelKey ? t(column.labelKey) : column.label);
  // The unit rides along so the create dialog opens with the unit already typed
  // into the row, matching the trip offer's `initialUnit` (#698).
  const [createDraft, setCreateDraft] = useState<{ rowKey: string; name: string; unit: string } | null>(null);

  // Column visibility, its persistence and the grid track string all come from
  // the shared sheet primitives (#911), so Food and Shopping remember columns
  // the same way — under separate keys, because they are separate questions.
  const { gridTemplate, toggleColumn: toggleColumnKey, visibleColumns, visibleKeys } = useSheetColumns({
    storageKey,
    columns: sheetColumns,
    desktopDefaults: desktopDefaultColumns,
    mobileDefaults: mobileDefaultColumns,
    // The on-hand chip shares the row-action cell with the delete button, so a
    // stock-aware sheet needs a wider action track (#1058).
    actionColumnWidth: onMarkOutOfStock ? SHEET_ACTION_COLUMN_WIDTH_WITH_STOCK : undefined
  });
  const [rows, setRows] = useState<Row[]>(() => seedRows(items));
  const [selection, setSelection] = useState<{ anchor: CellPos; focus: CellPos } | null>(null);
  const [dragging, setDragging] = useState(false);
  const [armedDeleteKey, setArmedDeleteKey] = useState<string | null>(null);
  const [offline, setOfflineState] = useState(false);
  // Is a sync on its way? State rather than the timer ref, because the footer
  // reads it during render and a ref changing does not re-render (#1122).
  const [syncScheduled, setSyncScheduled] = useState(false);

  const rowsRef = useRef(rows);
  const offlineRef = useRef(false);
  const pendingRef = useRef(0);
  const seedRef = useRef<{ listId: string | null; done: boolean }>({ listId: null, done: false });
  const pendingDeletesRef = useRef<EntityReferenceId[]>([]);
  /**
   * What a deleted row looked like, and where it sat, so Undo can put it back
   * (#296). Keyed by server id, because that is what the flush reports having
   * deleted and what the restore endpoint is addressed by.
   *
   * Held only between the tap and the toast: `claimRemoved` takes the entry out
   * as it goes into the toast's closure, so nothing accumulates here across a
   * session and a row can only be offered back once.
   */
  const removedRowsRef = useRef(new Map<string, RemovedRow>());
  /**
   * The create batch a retryable failure left ambiguous, by row key (#1235).
   *
   * Non-null means "a batch went up and we never learned whether it landed".
   * Until that is resolved the next pass sends THAT batch, unchanged, so it
   * arrives under the request id it already used and the server's ledger can
   * recognise it. Rows typed during the outage wait one pass; re-composing the
   * batch around them would mint a fresh id and duplicate the rows the lost
   * attempt had already committed.
   */
  const retryCreateKeysRef = useRef<readonly string[] | null>(null);
  const syncTimerRef = useRef<number | null>(null);
  const flushingRef = useRef(false);
  const flushAgainRef = useRef(false);
  const runFlushRef = useRef<() => Promise<void>>(async () => undefined);
  const dragStart = useRef<CellPos | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Where the caret is, so a language change does not rewrite the cell someone
  // is typing in.
  const caret = useCaretCell(containerRef);
  const commitRows = useCallback((next: Row[]) => {
    rowsRef.current = next;
    setRows(next);
  }, []);

  const mutateRows = useCallback((updater: (rows: Row[]) => Row[]) => {
    commitRows(updater(rowsRef.current));
  }, [commitRows]);

  // The language can change while this sheet is open — the preference syncs
  // from the user's other devices — and the numbers on screen are TEXT written
  // in the old one. Rewrite them, or the sheet shows a Spanish user English
  // numbers indefinitely and the first keystroke into such a cell re-reads the
  // whole old string as Spanish (codex review found that five ways).
  useEffect(() => onResolvedLocaleChange((to) => {
    mutateRows((current) => current.map((row) => ({
      ...row,
      ...reformatNumbers(row.cells, row.seeded, row.typedIn, to, (column) => caret.isComposing(`${row.key}:${column}`))
    })));
  }), [mutateRows, caret]);


  const setOffline = useCallback((value: boolean) => {
    offlineRef.current = value;
    setOfflineState(value);
  }, []);

  const hasSyncable = useCallback(() => {
    if (pendingDeletesRef.current.length) return true;
    return rowsRef.current.some((row) => row.dirty && !row.saving && (row.serverId != null || cellName(row) !== ""));
  }, []);

  // --- undo for a removed row (#296) ---------------------------------------

  const removedRowLabel = useCallback(
    (row: Row) => cellName(row) || t("sheet.removedRowUnnamed"),
    [t]
  );

  /**
   * Take the removal back: the server puts the row back FIRST, and only then
   * does it reappear here.
   *
   * That order is the whole point. Restoring the sheet optimistically and
   * letting the request follow would show the row back on a list it is not on —
   * the one state this feature exists to prevent — whenever the undo window has
   * closed, the connection has dropped, or the list itself has since been
   * deleted. A failed undo says so instead.
   *
   * The row returns under its ORIGINAL server id, so a second tap and a retried
   * request both land on the row that is already back rather than adding
   * another; `rowsWithRestored` declines to add a second copy here for the same
   * reason.
   */
  const undoRemovals = useCallback(async (removed: RemovedRow[]): Promise<void> => {
    const back: Array<{ entry: RemovedRow; item: SheetListItem | null }> = [];
    const failed: Row[] = [];
    for (const entry of removed) {
      if (entry.row.serverId == null) {
        back.push({ entry, item: null });
        continue;
      }
      try {
        // The item the server put back rides along: a cleared row needs its
        // contents, not just the news that the restore worked.
        const item = await sync.restore(entry.row.serverId);
        back.push({ entry, item });
      } catch {
        failed.push(entry.row);
      }
    }
    if (back.length) {
      // Oldest position first, so each row is placed before the ones that came
      // after it are measured against the list.
      const ordered = [...back].sort((left, right) => left.entry.index - right.entry.index);
      mutateRows((current) => ordered.reduce((rows, { entry, item }) => (
        entry.cleared
          ? rowsWithClearedRestored(rows, entry, item)
          : rowsWithRestored(rows, entry.row, entry.index)
      ), current));
      if (back.some(({ entry }) => entry.row.serverId != null)) sync.refreshContext();
    }
    // Named when there is one, counted when there are several: a list of names
    // in a toast is unreadable at the width this app is designed for.
    if (failed.length === 1) showToast(t("sheet.undoFailed", { name: removedRowLabel(failed[0]!) }));
    else if (failed.length) showToast(t("sheet.undoFailedCount", { count: failed.length }));
  }, [mutateRows, removedRowLabel, showToast, sync, t]);

  /**
   * Offer the way back — once the removals are REAL.
   *
   * Called from the flush, after the server has answered, rather than from the
   * tap. A toast raised at the tap would be offering to undo a delete that is
   * still sitting in the debounce, and Undo would then race the delete it was
   * meant to reverse: cancel the queued write and the row is restored twice
   * over, or lose the race and the restore arrives before the delete. Waiting
   * costs the toast one debounce interval and removes the race entirely.
   *
   * One toast for the whole pass, because there is only one toast slot: two
   * rows deleted inside a debounce window flush together, and announcing them
   * one at a time would leave the first row gone with its Undo already replaced
   * on screen. Its entry has left the map by then, so it would be gone for good
   * as far as this sheet is concerned.
   */
  const announceRemovals = useCallback((removed: RemovedRow[]) => {
    if (!removed.length) return;
    const message = removed.length === 1
      ? t("sheet.removedRow", { name: removedRowLabel(removed[0]!.row) })
      : t("sheet.removedRows", { count: removed.length });
    showToast(message, {
      actions: [{ label: t("sheet.undoRemoval"), onClick: () => undoRemovals(removed) }]
    });
  }, [removedRowLabel, showToast, t, undoRemovals]);

  /** The snapshot for an id the server has confirmed removed, taken out of the
   * map as it is handed over, so a row is offered back exactly once. Empty for
   * a row cleared rather than deleted: that row is still on screen. */
  const claimRemoved = useCallback((id: EntityReferenceId): RemovedRow | undefined => {
    const removed = removedRowsRef.current.get(String(id));
    if (removed) removedRowsRef.current.delete(String(id));
    return removed;
  }, []);

  // --- background sync ------------------------------------------------------

  // "unnamed" is a pass that reached the server but came back without an id for
  // a row it created. It cannot happen against today's bulk endpoint, which
  // returns one row per input — but the row stays dirty rather than being
  // retired (#1122), and a dirty create is what this loop re-sends. Retrying it
  // immediately against a server that keeps answering is how one lost id would
  // become a hundred rows, so the pass stops and waits for the next trigger.
  //
  // The footer reads "Saving…" until that trigger arrives, because the row is
  // dirty and the sheet is online — the one state in which that label is not
  // true. Deliberately left: the alternatives are to raise the offline banner
  // while demonstrably online, or to invent footer vocabulary in every language
  // for a response the API cannot produce. Any keystroke, reconnect or unmount
  // corrects it, and the safe half — the row is kept, not duplicated, not
  // silently retired — is what matters here.
  const flushOnce = useCallback(async (): Promise<"ok" | "offline" | "empty" | "unnamed"> => {
    const snapshot = rowsRef.current;
    const createPairs = createCandidates(snapshot, retryCreateKeysRef.current);
    const updates = snapshot.filter((row) => row.dirty && !row.saving && row.serverId != null && cellName(row) !== "");
    const clears = snapshot.filter((row) => row.dirty && !row.saving && row.serverId != null && cellName(row) === "");
    const trashed = pendingDeletesRef.current.slice();

    if (!createPairs.length && !updates.length && !clears.length && !trashed.length) return "empty";

    const sig = new Map<string, string>();
    for (const { row } of createPairs) sig.set(row.key, rowSig(row));
    for (const row of updates) sig.set(row.key, rowSig(row));
    for (const row of clears) sig.set(row.key, rowSig(row));
    mutateRows((current) => current.map((row) => (sig.has(row.key) ? { ...row, saving: true } : row)));

    let unnamed = false;
    // Collected as the deletes land and announced once the pass is over,
    // whichever way it ended (#296). A pass that throws after two of three
    // deletes committed still owes the user an Undo for those two.
    const undoable: RemovedRow[] = [];
    try {
      if (createPairs.length) {
        // The row keys ride along so the retry of a lost attempt is recognisable
        // as the same request (#1235): they are what says "these rows again",
        // as opposed to a second batch that happens to hold the same words.
        const added = await sync.createBulk(createPairs.map((pair) => pair.payload), createPairs.map((pair) => pair.row.key));
        // Answered. Whatever happens to these rows now, the next pass composes
        // its batch freshly rather than re-sending this one.
        retryCreateKeysRef.current = null;
        unnamed = createPairs.some((_pair, index) => added[index]?.id == null);
        mutateRows((current) => current.map((row) => {
          const index = createPairs.findIndex((pair) => pair.row.key === row.key);
          if (index < 0) return row;
          return rowAfterCreate(row, createPairs[index], added[index]?.id ?? null, rowSig(row) === sig.get(row.key));
        }));
      }
      for (const row of updates) {
        const payload = fieldsFromCells(row.cells, row.productId, row.seeded, row.typedIn);
        await sync.update(row.serverId as EntityReferenceId, payload);
        mutateRows((current) => current.map((candidate) => (
          candidate.key === row.key
            ? {
              ...candidate,
              saving: false,
              dirty: rowSig(candidate) !== sig.get(row.key),
              seeded: seedFromPayload(payload, getResolvedLocale())
            }
            : candidate
        )));
      }
      for (const row of clears) {
        const clearedFrom = snapshot.indexOf(row);
        await sync.remove(row.serverId as EntityReferenceId);
        // Same offer as an outright delete, and for a stronger reason: this row
        // is no longer on anybody's list, but it is still sitting on screen, so
        // without the toast there is nothing at all to tell the user the item
        // is gone (#296).
        undoable.push({ row, index: clearedFrom < 0 ? snapshot.length : clearedFrom, cleared: true });
        mutateRows((current) => current.map((candidate) => (
          candidate.key === row.key ? { ...candidate, serverId: null, saving: false, dirty: cellName(candidate) !== "" } : candidate
        )));
      }
      for (const id of trashed) {
        await sync.remove(id);
        pendingDeletesRef.current = pendingDeletesRef.current.filter((pending) => pending !== id);
        // Claimed as each one lands, announced together below: the removals are
        // now real for everyone, so the Undo offered is one the server can
        // honour (#296).
        const removed = claimRemoved(id);
        if (removed) undoable.push(removed);
      }
      // The server answered, so the connection is real whatever else happened.
      setOffline(false);
      announceRemovals(undoable);
      return unnamed ? "unnamed" : "ok";
    } catch (error) {
      mutateRows((current) => current.map((row) => (sig.has(row.key) ? { ...row, saving: false } : row)));
      announceRemovals(undoable);
      if (isRetryableSyncError(error)) {
        // The create batch is now the ambiguous one — it may have committed on
        // the server with only its response lost — so the next pass has to send
        // it again AS ITSELF, under the id it already used (#1235).
        if (createPairs.length) retryCreateKeysRef.current = createPairs.map((pair) => pair.row.key);
        setOffline(true);
        return "offline";
      }
      // Permanent rejection: stop retrying these rows so we do not spin.
      retryCreateKeysRef.current = null;
      mutateRows((current) => current.map((row) => (sig.has(row.key) ? { ...row, dirty: false } : row)));
      return "ok";
    }
  }, [announceRemovals, claimRemoved, mutateRows, setOffline, sync]);

  const runFlush = useCallback(async () => {
    if (flushingRef.current) {
      flushAgainRef.current = true;
      return;
    }
    flushingRef.current = true;
    try {
      for (;;) {
        flushAgainRef.current = false;
        const result = await flushOnce();
        if (result === "offline" || result === "unnamed") break;
        if (!flushAgainRef.current && !hasSyncable()) break;
      }
    } finally {
      flushingRef.current = false;
      // Whether anything is still coming for the rows that remain unsaved. A
      // keystroke during the flush will have set a fresh timer, so this is read
      // rather than assumed — the footer's "Saving…" is a claim about the
      // future, and after a pass that stopped (offline, unnamed) nothing is
      // coming until the next trigger (#1122).
      setSyncScheduled(syncTimerRef.current !== null);
    }
    if (!offlineRef.current && !hasSyncable()) sync.refreshContext();
  }, [flushOnce, hasSyncable, sync]);

  useEffect(() => {
    runFlushRef.current = runFlush;
  }, [runFlush]);

  const scheduleSync = useCallback(() => {
    if (syncTimerRef.current !== null) window.clearTimeout(syncTimerRef.current);
    setSyncScheduled(true);
    syncTimerRef.current = window.setTimeout(() => {
      syncTimerRef.current = null;
      void runFlushRef.current();
    }, SYNC_DEBOUNCE_MS);
  }, []);

  const flushNow = useCallback(() => {
    if (syncTimerRef.current !== null) {
      window.clearTimeout(syncTimerRef.current);
      syncTimerRef.current = null;
    }
    if (hasSyncable()) {
      setSyncScheduled(true);
      void runFlushRef.current();
    }
  }, [hasSyncable]);

  useEffect(() => {
    if (!flushControl) return undefined;
    flushControl.current = async () => {
      if (syncTimerRef.current !== null) {
        window.clearTimeout(syncTimerRef.current);
        syncTimerRef.current = null;
      }
      if (hasSyncable()) await runFlushRef.current();
      return !hasSyncable();
    };
    return () => {
      flushControl.current = null;
    };
  }, [flushControl, hasSyncable]);

  // --- seeding from server props (only on list switch / first arrival) ------

  useEffect(() => {
    const lid = listId ?? null;
    if (seedRef.current.listId !== lid) {
      pendingDeletesRef.current = [];
      // A frozen retry batch names rows of the list being left behind.
      retryCreateKeysRef.current = null;
      commitRows(seedRows(items));
      seedRef.current = { listId: lid, done: items.length > 0 };
      return;
    }
    if (!seedRef.current.done && items.length > 0 && !hasSyncable()) {
      commitRows(seedRows(items));
      seedRef.current.done = true;
    }
  }, [commitRows, hasSyncable, items, listId]);

  // --- connection + unsaved lifecycle --------------------------------------

  useEffect(() => {
    const onOnline = () => {
      setOffline(false);
      void runFlushRef.current();
    };
    const onOffline = () => setOffline(true);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
    };
  }, [setOffline]);

  const pendingCount = rows.filter((row) => (row.dirty || row.saving) && (row.serverId != null || cellName(row) !== "")).length
    + pendingDeletesRef.current.length;
  pendingRef.current = pendingCount;

  useEffect(() => {
    const warnUnsaved = (event: BeforeUnloadEvent) => {
      if (pendingRef.current > 0) {
        event.preventDefault();
        event.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", warnUnsaved);
    // Flush anything still pending when the sheet unmounts (navigating away).
    return () => {
      window.removeEventListener("beforeunload", warnUnsaved);
      if (syncTimerRef.current !== null) window.clearTimeout(syncTimerRef.current);
      if (hasSyncable()) void runFlushRef.current();
    };
  }, [hasSyncable]);

  // --- editing --------------------------------------------------------------

  const editCell = useCallback((key: string, columnKey: ListColumnKey, value: string) => {
    seedRef.current.done = true;
    mutateRows((current) => ensureTrailingBlank(current.map((row) => {
      if (row.key !== key) return row;
      // Typing over a linked name unlinks it — free text no longer matches
      // the catalog definition it once carried (#635).
      const productId = columnKey === "name" ? null : row.productId;
      // Stamped at the keystroke, and only on the column actually written: a row
      // holds two independent drafts, so editing the NAME must not relabel a
      // quantity the user typed before the language changed.
      // The cell's own stamp is the evidence, not the app's current language:
      // text keeps the language it was written in until someone clears it.
      const typedIn = isStampedColumn(columnKey)
        ? { ...row.typedIn, [columnKey]: stampForEdit(row.cells[columnKey] ?? "", row.typedIn[columnKey]) }
        : row.typedIn;
      return { ...row, cells: { ...row.cells, [columnKey]: value }, dirty: true, productId, typedIn };
    })));
    scheduleSync();
  }, [mutateRows, scheduleSync]);

  // Link a row to a catalog product definition (#635): the picked name always
  // wins, empty unit/section cells inherit the definition's defaults, and
  // anything the shopper already typed there is left alone.
  const pickDefinition = useCallback((key: string, product: Product) => {
    seedRef.current.done = true;
    mutateRows((current) => ensureTrailingBlank(current.map((row) => {
      if (row.key !== key) return row;
      const cells = { ...row.cells, name: product.name };
      if (!(cells.unit ?? "").trim() && product.defaultUnit) cells.unit = product.defaultUnit;
      if (!(cells.section ?? "").trim() && product.storeSection) cells.section = product.storeSection;
      return { ...row, cells, dirty: true, productId: product.id };
    })));
    scheduleSync();
  }, [mutateRows, scheduleSync]);

  useEffect(() => {
    if (!addControl) return undefined;
    addControl.current = (name: string) => {
      const trimmed = name.trim().slice(0, 200);
      if (!trimmed) return;
      seedRef.current.done = true;
      const locale = getResolvedLocale();
      mutateRows((current) => {
        // The named row takes the trailing blank's place, and a fresh blank
        // follows it, so the sheet reads as it would after typing there.
        const last = current[current.length - 1];
        const kept = last && rowIsBlank(last) ? current.slice(0, -1) : current;
        return ensureTrailingBlank([...kept, {
          key: nextKey(),
          serverId: null,
          cells: { name: trimmed },
          dirty: true,
          saving: false,
          productId: null,
          seeded: emptySeed(locale),
          typedIn: stampAll(locale)
        }]);
      });
      scheduleSync();
    };
    return () => {
      addControl.current = null;
    };
  }, [addControl, mutateRows, scheduleSync]);

  function requestCreateDefinition(key: string, name: string) {
    const row = rowsRef.current.find((candidate) => candidate.key === key);
    setCreateDraft({ rowKey: key, name, unit: (row?.cells.unit ?? "").trim() });
  }

  function handleDefinitionSaved(product: Product) {
    const draft = createDraft;
    setCreateDraft(null);
    if (draft) pickDefinition(draft.rowKey, product);
    showToast(t("sheet.addedToCatalog", { name: product.name }), { link: toastLinkForEntity(t("sheet.openCatalog"), "product", product.id) });
  }

  // The duplicate warning's action (#698). In the sheet the shopper's goal is
  // to point this row at the food that already exists, not to go edit its
  // definition — so link the row instead of dead-ending on a closed dialog.
  function handleDefinitionDuplicate(existing: Product) {
    const draft = createDraft;
    setCreateDraft(null);
    if (draft) pickDefinition(draft.rowKey, existing);
    showToast(t("sheet.linkedToCatalog", { name: existing.name }), { link: toastLinkForEntity(t("sheet.openCatalog"), "product", existing.id) });
  }

  function appendBlankRow() {
    mutateRows((current) => [...current, blankRow()]);
  }

  function deleteRow(key: string) {
    const index = rowsRef.current.findIndex((candidate) => candidate.key === key);
    const row = index < 0 ? undefined : rowsRef.current[index];
    if (!row) return;
    setArmedDeleteKey(null);
    if (row.serverId != null) {
      pendingDeletesRef.current = [...pendingDeletesRef.current, row.serverId];
      // Kept for the toast the flush raises once the server has answered — see
      // `announceRemovals` for why the offer waits (#296).
      removedRowsRef.current.set(String(row.serverId), { row, index, cleared: false });
    }
    mutateRows((current) => ensureTrailingBlank(current.filter((candidate) => candidate.key !== key)));
    if (row.serverId != null) {
      scheduleSync();
      return;
    }
    // A row the server never saw: nothing to reverse, so the offer can be made
    // straight away. It is still worth making — what was typed into it left the
    // screen exactly as visibly as a saved row does.
    if (!rowIsBlank(row)) {
      announceRemovals([{ row, index, cleared: false }]);
    }
  }

  // Row delete is a two-tap confirm (first tap arms, second deletes). Auto-disarm
  // after a few seconds so a stray first tap can never be confirmed later by an
  // accidental tap on the same row.
  useEffect(() => {
    if (armedDeleteKey === null) return undefined;
    const handle = window.setTimeout(() => setArmedDeleteKey(null), 3500);
    return () => window.clearTimeout(handle);
  }, [armedDeleteKey]);

  useEffect(() => {
    const endDrag = () => {
      dragStart.current = null;
      setDragging(false);
    };
    document.addEventListener("mouseup", endDrag);
    return () => document.removeEventListener("mouseup", endDrag);
  }, []);

  // Persistence moved into useSheetColumns (#911), which writes on TOGGLE
  // rather than on mount. The old effect stored whatever the responsive default
  // resolved to, so one visit from a phone permanently narrowed the desktop
  // sheet to a single column; now only a choice the user actually made is kept.

  const range = useMemo(() => {
    if (!selection) return null;
    const r1 = Math.min(selection.anchor.row, selection.focus.row);
    const r2 = Math.max(selection.anchor.row, selection.focus.row);
    const c1 = Math.min(selection.anchor.col, selection.focus.col);
    const c2 = Math.max(selection.anchor.col, selection.focus.col);
    if (r1 === r2 && c1 === c2) return null;
    return { r1, r2, c1, c2 };
  }, [selection]);

  function focusCell(row: number, col: number) {
    window.setTimeout(() => {
      const input = containerRef.current?.querySelector<HTMLInputElement>(`input[data-row="${row}"][data-col="${col}"]`);
      input?.focus();
      input?.select();
    }, 0);
  }

  function displayValue(row: number, col: number): string {
    const column = visibleColumns[col];
    return column ? rows[row]?.cells[column.key] ?? "" : "";
  }

  function startCellPress(pos: CellPos) {
    dragStart.current = pos;
    setSelection(null);
    setArmedDeleteKey(null);
  }

  function extendCellDrag(pos: CellPos, buttons: number) {
    const start = dragStart.current;
    if (!start || !(buttons & 1)) return;
    if (start.row === pos.row && start.col === pos.col) {
      setSelection(null);
      return;
    }
    if (!dragging) {
      setDragging(true);
      window.getSelection()?.removeAllRanges();
      const active = document.activeElement;
      if (active instanceof HTMLElement) active.blur();
      containerRef.current?.focus();
    }
    setSelection({ anchor: start, focus: pos });
  }

  function handleSheetKeyDown(event: ReactKeyboardEvent<HTMLDivElement>) {
    if (event.key === "Escape") {
      if (selection) {
        event.stopPropagation();
        setSelection(null);
      }
      return;
    }
    if (!range) return;
    if (event.key === "Delete" || event.key === "Backspace") {
      event.preventDefault();
      setSelection(null);
      clearRange(range);
    }
  }

  function handleCopy(event: ReactClipboardEvent<HTMLDivElement>) {
    if (!range) return;
    event.preventDefault();
    const lines: string[] = [];
    for (let row = range.r1; row <= range.r2; row++) {
      const cells: string[] = [];
      for (let col = range.c1; col <= range.c2; col++) cells.push(displayValue(row, col));
      lines.push(cells.join("\t"));
    }
    event.clipboardData.setData("text/plain", lines.join("\n"));
  }

  function clearRange(target: { r1: number; r2: number; c1: number; c2: number }) {
    mutateRows((current) => current.map((row, index) => {
      if (index < target.r1 || index > target.r2) return row;
      const cells = { ...row.cells };
      const cleared: NumericColumn[] = [];
      let changed = false;
      for (let col = target.c1; col <= target.c2; col++) {
        const column = visibleColumns[col];
        if (column && (cells[column.key] ?? "") !== "") {
          cells[column.key] = "";
          if (isNumericColumn(column.key)) cleared.push(column.key);
          changed = true;
        }
      }
      if (!changed) return row;
      // An empty cell reads the same in every language, so its old stamp is
      // meaningless; resetting it keeps the row from carrying a label for text
      // that is no longer there.
      const typedIn = { ...row.typedIn };
      for (const column of cleared) typedIn[column] = getResolvedLocale();
      return { ...row, cells, dirty: true, typedIn };
    }));
    scheduleSync();
  }

  function handleCellPaste(event: ReactClipboardEvent<HTMLInputElement>, pos: CellPos) {
    // Shared with the Food sheet (#911): a payload with no tab and no newline
    // is ordinary typing and is left to the input.
    const block = parseSheetPaste(event.clipboardData.getData("text/plain"));
    if (!block) return;
    event.preventDefault();
    pasteGrid(block, pos);
  }

  function pasteGrid(block: string[][], pos: CellPos) {
    seedRef.current.done = true;
    mutateRows((current) => {
      const next = current.slice();
      block.forEach((cellTexts, offset) => {
        const rowIndex = pos.row + offset;
        const line = cellTexts.join("\t");
        if (rowIndex < next.length) {
          const cells = { ...next[rowIndex]!.cells };
          let touchedName = false;
          // Pasted text arrives NOW, so it carries today's convention — but only
          // for the columns the paste actually covers. A paste that lands on the
          // name must leave a quantity typed under another language labelled as
          // that language.
          const typedIn = { ...next[rowIndex]!.typedIn };
          cellTexts.forEach((text, columnOffset) => {
            const column = visibleColumns[pos.col + columnOffset];
            if (!column) return;
            cells[column.key] = text;
            // Every stamped column, not only the numbers: the NAME's stamp is
            // what decides whether the note parser may read the line, so a
            // pasted name must carry the language it was pasted in.
            if (isStampedColumn(column.key)) typedIn[column.key] = getResolvedLocale();
            if (column.key === "name") touchedName = true;
          });
          // Pasting over the name unlinks any catalog definition the row
          // carried, same as typing over it by hand (#635).
          const productId = touchedName ? null : next[rowIndex]!.productId;
          next[rowIndex] = { ...next[rowIndex]!, cells, dirty: true, productId, typedIn };
        } else if (line.trim()) {
          const cells: Cells = {};
          cellTexts.forEach((text, columnOffset) => {
            const column = visibleColumns[pos.col + columnOffset];
            if (column) cells[column.key] = text;
          });
          next.push({ key: nextKey(), serverId: null, cells, dirty: true, saving: false, productId: null, seeded: emptySeed(getResolvedLocale()), typedIn: stampAll(getResolvedLocale()) });
        }
      });
      return ensureTrailingBlank(next);
    });
    scheduleSync();
  }

  function toggleColumn(key: ListColumnKey) {
    // The selection is column-index based, so it cannot survive the columns
    // moving underneath it.
    setSelection(null);
    toggleColumnKey(key);
  }

  function isSelected(pos: CellPos): boolean {
    return Boolean(range && pos.row >= range.r1 && pos.row <= range.r2 && pos.col >= range.c1 && pos.col <= range.c2);
  }

  function handleCellEnter(pos: CellPos) {
    if (pos.row >= rowsRef.current.length - 1) appendBlankRow();
    focusCell(pos.row + 1, pos.col);
  }

  const totalMinor = rowsTotalMinor(rows);
  const itemCount = rows.filter((row) => cellName(row) !== "").length;
  // Same three-state vocabulary the Food sheet reports (#911); only the
  // in-flight wording differs, because this sheet syncs in the background while
  // Food commits on demand.
  const status = sheetSyncStatusLabel({
    offline,
    pendingCount,
    // In flight (a row is mid-request) or on its way (the debounce is running).
    // Neither is true after a pass that stopped, and the footer must not go on
    // promising a save that is not coming (#1122).
    busy: syncScheduled || rows.some((row) => row.saving),
    idleLabel: t("sheet.status.saved"),
    pendingLabel: () => t("sheet.status.saving"),
    offlineLabel: t("sheet.status.offline"),
    offlineUnsavedLabel: (count) => t("sheet.status.offlineUnsaved", { count }),
    unsavedLabel: (count) => t("sheet.status.unsaved", { count })
  });

  return (
    <div
      ref={containerRef}
      className={dragging ? "list-sheet dragging" : "list-sheet"}
      tabIndex={-1}
      onCopy={handleCopy}
      onKeyDown={handleSheetKeyDown}
      onBlur={(event) => {
        if (event.relatedTarget instanceof Node && event.currentTarget.contains(event.relatedTarget)) return;
        flushNow();
      }}
    >
      {offline ? <SheetOfflineBanner /> : null}

      <ListSheetToolbar>
        <SheetColumnsMenu columns={sheetColumns} visibleKeys={visibleKeys} onToggle={toggleColumn} />
      </ListSheetToolbar>

      <div className="list-sheet-scroll">
        <div className="list-sheet-grid" role="grid" aria-label={t("sheet.grid")}>
          <div className="list-sheet-row list-sheet-head" role="row" style={{ gridTemplateColumns: gridTemplate }}>
            {visibleColumns.map((column) => (
              <span key={column.key} role="columnheader" data-key={column.key}>{columnLabel(column)}</span>
            ))}
            <span aria-hidden="true" />
          </div>

          {rows.map((row, rowIndex) => {
            const isLastRow = rowIndex === rows.length - 1;
            const blank = rowIsBlank(row);
            return (
              <div key={row.key} className={blank ? "list-sheet-row draft" : "list-sheet-row"} role="row" style={{ gridTemplateColumns: gridTemplate }}>
                {visibleColumns.map((column, col) => {
                  const linkUrl = column.key === "link" ? safeCellUrl(row.cells.link) : "";
                  if (column.key === "name" && definitions) {
                    return (
                      <NameCell
                        key={column.key}
                        ariaLabel={t("sheet.cellLabel", {
                          column: columnLabel(column),
                          name: cellName(row) || t("sheet.rowNumber", { number: rowIndex + 1 })
                        })}
                        column={column}
                        dataCell={`${row.key}:${column.key}`}
                        definitions={definitions}
                        placeholder={isLastRow ? t("sheet.addItem") : undefined}
                        pos={{ row: rowIndex, col }}
                        productId={row.productId}
                        selected={isSelected({ row: rowIndex, col })}
                        value={row.cells[column.key] ?? ""}
                        onChangeValue={(value) => editCell(row.key, column.key, value)}
                        onEnter={handleCellEnter}
                        onMouseDownCell={startCellPress}
                        onMouseEnterCell={extendCellDrag}
                        onPasteCell={handleCellPaste}
                        onPickDefinition={(product) => pickDefinition(row.key, product)}
                        onRequestCreate={(name) => requestCreateDefinition(row.key, name)}
                      />
                    );
                  }
                  return (
                    <PositionedSheetCell
                      key={column.key}
                      ariaLabel={t("sheet.cellLabel", {
                        column: columnLabel(column),
                        name: cellName(row) || t("sheet.rowNumber", { number: rowIndex + 1 })
                      })}
                      column={column}
                      dataCell={`${row.key}:${column.key}`}
                      placeholder={column.key === "name" && isLastRow ? t("sheet.addItem") : undefined}
                      pos={{ row: rowIndex, col }}
                      selected={isSelected({ row: rowIndex, col })}
                      value={row.cells[column.key] ?? ""}
                      onChangeValue={(value) => editCell(row.key, column.key, value)}
                      onEnter={handleCellEnter}
                      onMouseDownCell={startCellPress}
                      onMouseEnterCell={extendCellDrag}
                      onPasteCell={handleCellPaste}
                      trailing={linkUrl ? (
                        <a
                          className="list-sheet-cell-link"
                          href={linkUrl}
                          target="_blank"
                          rel="noreferrer"
                          tabIndex={-1}
                          aria-label={t("sheet.openLink", { name: cellName(row) })}
                        >
                          <ExternalLink size={13} />
                        </a>
                      ) : null}
                    />
                  );
                })}
                <span className={onMarkOutOfStock ? "list-sheet-row-action list-sheet-row-action-stock" : "list-sheet-row-action"}>
                  {(() => {
                    // On-hand rides in the row's own action cell rather than a
                    // column: it is not an editable field, and a column would
                    // cost horizontal room on the phone this list is built on.
                    if (blank || !onMarkOutOfStock || !definitions || !row.productId) return null;
                    const linked = definitions.find((product) => String(product.id) === String(row.productId));
                    const label = onHandLabel(linked);
                    if (!label) return null;
                    return (
                      <button
                        type="button"
                        className="list-sheet-onhand"
                        data-product-id={String(row.productId)}
                        // Deliberately NOT tabIndex={-1}, unlike the delete
                        // button beside it (codex review). Delete has a keyboard
                        // route through the sheet's own key handling; this is
                        // the only way to reach "we're out", so removing it from
                        // the tab order would put the action out of reach for
                        // keyboard-only users entirely.
                        title={t("sheet.onHandTitle", { amount: label, name: linked?.name ?? cellName(row) })}
                        aria-label={t("sheet.onHandLabel", { amount: label, name: linked?.name ?? cellName(row) })}
                        onMouseDown={(event) => event.preventDefault()}
                        onClick={() => { if (linked) void onMarkOutOfStock(linked); }}
                      >
                        <Package size={12} aria-hidden="true" />
                        <span>{label}</span>
                      </button>
                    );
                  })()}
                  {blank ? null : (
                    <button
                      type="button"
                      className={armedDeleteKey === row.key ? "icon-button danger-armed" : "icon-button danger-action"}
                      tabIndex={-1}
                      aria-label={armedDeleteKey === row.key
                        ? t("sheet.confirmDeleteRow", { name: cellName(row) })
                        : t("sheet.deleteRow", { name: cellName(row) })}
                      title={armedDeleteKey === row.key ? t("sheet.clickAgainToDelete") : t("sheet.deleteItem")}
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={() => (armedDeleteKey === row.key ? deleteRow(row.key) : setArmedDeleteKey(row.key))}
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      <footer className="list-sheet-footer">
        <span>{t("sheet.items", { count: itemCount })}</span>
        <span className={offline ? "list-sheet-status offline" : "list-sheet-status"} aria-live="polite">{status}</span>
        <span className="list-sheet-total">{t("sheet.total")} <strong>{money(totalMinor, currency)}</strong></span>
      </footer>

      <datalist id={unitDatalistId}>
        {standardUnits.map((unit) => <option key={unit} value={unit} />)}
      </datalist>

      {definitions ? (
        <ProductDefinitionDialog
          open={createDraft != null}
          mode="create"
          initialName={createDraft?.name ?? ""}
          initialUnit={createDraft?.unit ?? ""}
          locations={locations}
          products={definitions}
          duplicateAction="link"
          onSaved={handleDefinitionSaved}
          onClose={() => setCreateDraft(null)}
          onJumpToEdit={handleDefinitionDuplicate}
        />
      ) : null}
    </div>
  );
}

/**
 * Position-addressed wrapper over the shared `SheetCell` (#911).
 *
 * The shared component owns the markup, the classes and the focus behaviour;
 * this adapter translates between it and the sheet's `(row, col)` coordinate
 * system, which the rectangular drag-selection and copy features depend on and
 * which Food — a row-id-addressed grid — has no use for.
 */
function PositionedSheetCell({
  ariaLabel,
  column,
  dataCell,
  onChangeValue,
  onEnter,
  onMouseDownCell,
  onMouseEnterCell,
  onPasteCell,
  placeholder,
  pos,
  selected,
  trailing,
  value
}: {
  ariaLabel: string;
  column: ColumnDef;
  /** `<rowKey>:<column>` — what {@link useCaretCell} identifies a cell by. */
  dataCell: string;
  onChangeValue: (value: string) => void;
  onEnter: (pos: CellPos) => void;
  onMouseDownCell: (pos: CellPos) => void;
  onMouseEnterCell: (pos: CellPos, buttons: number) => void;
  onPasteCell: (event: ReactClipboardEvent<HTMLInputElement>, pos: CellPos) => void;
  placeholder?: string;
  pos: CellPos;
  selected: boolean;
  trailing?: ReactNode;
  value: string;
}) {
  return (
    <SheetCell
      ariaLabel={ariaLabel}
      column={column}
      dataCell={dataCell}
      dataRow={pos.row}
      dataCol={pos.col}
      placeholder={placeholder}
      selected={selected}
      trailing={trailing}
      value={value}
      onChangeValue={onChangeValue}
      onMouseDownCell={() => onMouseDownCell(pos)}
      onMouseEnterCell={(event) => onMouseEnterCell(pos, event.buttons)}
      onPaste={(event) => onPasteCell(event, pos)}
      onKeyDown={(event) => {
        if (event.key !== "Enter") return;
        event.preventDefault();
        onEnter(pos);
      }}
    />
  );
}

const MAX_DEFINITION_MATCHES = 8;

// Exported for tests: the web unit suite renders in a plain Node environment
// (no DOM, no simulated focus events), so the matching logic behind the
// dropdown is verified directly rather than through a rendered interaction.
export function matchDefinitions(query: string, definitions: Product[]): Product[] {
  const trimmed = query.trim();
  if (!trimmed) return [];
  const normalizedQuery = normalizeFoodName(trimmed);
  const lowerQuery = trimmed.toLowerCase();
  return definitions
    .filter((product) => {
      const normalizedName = normalizeFoodName(product.name);
      return (
        (normalizedQuery && (normalizedName.includes(normalizedQuery) || normalizedQuery.includes(normalizedName)))
        || product.name.toLowerCase().includes(lowerQuery)
      );
    })
    .slice(0, MAX_DEFINITION_MATCHES);
}

// The item-name cell doubles as a searchable, creatable product-definition
// picker (#635). It renders exactly like SheetCell (same commit-on-Enter,
// paste, drag-select behavior) with one addition: while the dropdown of
// matches is open, ArrowUp/ArrowDown/Enter act on the dropdown instead of the
// grid, and the first Escape only closes the dropdown.
export function NameCell({
  ariaLabel,
  column,
  dataCell,
  definitions,
  initialOpen = false,
  onChangeValue,
  onEnter,
  onMouseDownCell,
  onMouseEnterCell,
  onPasteCell,
  onPickDefinition,
  onRequestCreate,
  placeholder,
  pos,
  productId,
  selected,
  value
}: {
  ariaLabel: string;
  column: ColumnDef;
  /** `<rowKey>:name` — the name is a stamped column, so its cell latches too. */
  dataCell: string;
  definitions: Product[];
  /** Seeds the dropdown's initial open state. Only meaningful for tests: the
   * unit suite has no DOM/focus events to drive `open` normally, so this lets
   * a static-markup render assert the open dropdown directly. Production
   * callers rely on focus/typing instead and never pass this. */
  initialOpen?: boolean;
  onChangeValue: (value: string) => void;
  onEnter: (pos: CellPos) => void;
  onMouseDownCell: (pos: CellPos) => void;
  onMouseEnterCell: (pos: CellPos, buttons: number) => void;
  onPasteCell: (event: ReactClipboardEvent<HTMLInputElement>, pos: CellPos) => void;
  onPickDefinition: (product: Product) => void;
  onRequestCreate: (name: string) => void;
  placeholder?: string;
  pos: CellPos;
  productId: EntityReferenceId | null;
  selected: boolean;
  value: string;
}) {
  const { t } = useTranslation("common");
  const [open, setOpen] = useState(initialOpen);
  // -1 = nothing highlighted: Enter/Tab then commit the typed free text
  // instead of silently cataloging the first fuzzy match (codex review).
  const [activeIndex, setActiveIndex] = useState(-1);
  const wrapRef = useRef<HTMLSpanElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const listId = useId();

  const matches = useMemo(() => matchDefinitions(value, definitions), [definitions, value]);
  const exactMatch = matches.some((product) => product.name.trim().toLowerCase() === value.trim().toLowerCase());
  const showCreateAction = open && value.trim().length > 0 && !exactMatch;
  const optionCount = matches.length + (showCreateAction ? 1 : 0);

  useEffect(() => {
    setActiveIndex(-1);
  }, [value, open]);

  function pick(product: Product) {
    onPickDefinition(product);
    setOpen(false);
  }

  function requestCreate() {
    onRequestCreate(value.trim());
    setOpen(false);
  }

  function onKeyDown(event: ReactKeyboardEvent<HTMLInputElement>) {
    if (open && (event.key === "ArrowDown" || event.key === "ArrowUp")) {
      event.preventDefault();
      if (!optionCount) return;
      const delta = event.key === "ArrowDown" ? 1 : -1;
      setActiveIndex((current) => {
        if (current < 0) return event.key === "ArrowDown" ? 0 : optionCount - 1;
        return (current + delta + optionCount) % optionCount;
      });
      return;
    }
    if (event.key === "Enter") {
      if (open && optionCount > 0 && activeIndex >= 0) {
        event.preventDefault();
        if (activeIndex < matches.length) pick(matches[activeIndex]!);
        else requestCreate();
        return;
      }
      event.preventDefault();
      setOpen(false);
      onEnter(pos);
      return;
    }
    if (event.key === "Escape") {
      // First Escape only dismisses the dropdown. Once it's already closed, a
      // second Escape "cancels the cell" by dropping focus (the grid has no
      // separate draft state to revert — every keystroke is already live).
      event.preventDefault();
      if (open) {
        event.stopPropagation();
        setOpen(false);
      } else {
        event.currentTarget.blur();
      }
      return;
    }
    if (event.key === "Tab" && open) {
      // Commit an EXPLICITLY highlighted option before focus leaves, without
      // blocking the tab itself. With nothing highlighted the typed free text
      // commits as-is — Tab must never silently catalog a fuzzy match.
      if (activeIndex >= 0 && activeIndex < matches.length && matches[activeIndex]) onPickDefinition(matches[activeIndex]);
      setOpen(false);
    }
  }

  const cataloged = Boolean(productId);
  const dropdownVisible = open && (matches.length > 0 || showCreateAction);
  const activeDescendant = dropdownVisible && activeIndex >= 0
    ? activeIndex < matches.length ? `${listId}-option-${activeIndex}` : `${listId}-create`
    : undefined;

  // The sheet scrolls horizontally, and `overflow-x: auto` forces the vertical
  // axis to clip too (visible computes to auto) — so an in-flow dropdown gets
  // cut off near the bottom rows. Render the menu position:fixed, measured off
  // the input, which escapes ancestor overflow entirely (codex review).
  //
  // Placement is the shared helper (#1017), the same one Food's picker uses:
  // a bare `top: rect.bottom + 2` with no height bound ran the menu off the
  // screen on a phone — measured at 360×640 on the grid's blank spare row,
  // 314px of a 358px menu sat below the fold and NONE of the nine options could
  // be tapped. It now sizes itself to the visual viewport, flips above the
  // input when below is cramped, and re-places on a keyboard resize instead of
  // closing at the moment the user starts typing.
  const { menuRef, menuStyle } = useSheetMenuPlacement({
    visible: dropdownVisible,
    anchorRef: inputRef,
    containerRef: wrapRef,
    minWidth: 240,
    onDismiss: () => setOpen(false)
  });

  return (
    <span
      ref={wrapRef}
      className={`list-sheet-cell${selected ? " sel" : ""}${cataloged ? " sheet-cell-cataloged" : ""}`}
      role="gridcell"
      data-key={column.key}
      onMouseDown={(event) => {
        if (event.button === 0) onMouseDownCell(pos);
      }}
      onMouseEnter={(event) => onMouseEnterCell(pos, event.buttons)}
    >
      <input
        ref={inputRef}
        data-cell={dataCell}
        data-row={pos.row}
        data-col={pos.col}
        aria-label={ariaLabel}
        aria-expanded={dropdownVisible}
        aria-autocomplete="list"
        aria-controls={listId}
        aria-activedescendant={activeDescendant}
        role="combobox"
        value={value}
        placeholder={placeholder}
        autoComplete="off"
        onChange={(event) => {
          onChangeValue(event.currentTarget.value);
          setOpen(true);
        }}
        // A row that already resolved to a catalog product has nothing to pick
        // (#703): opening the matches menu on focus made Enter-navigating down
        // a cataloged list flash a dropdown on every filled row. Typing still
        // reopens it via onChange, so re-picking stays one keystroke away.
        onFocus={() => { if (!cataloged) setOpen(true); }}
        onBlur={() => setOpen(false)}
        onKeyDown={onKeyDown}
        onPaste={(event) => onPasteCell(event, pos)}
      />
      {cataloged ? <Package className="sheet-cell-cataloged-icon" size={13} aria-hidden="true" /> : null}
      {dropdownVisible ? (
        <div
          ref={menuRef}
          className="placement-combobox-results sheet-cell-results"
          id={listId}
          role="listbox"
          aria-label={t("sheet.matchingItems")}
          style={menuStyle}
          // Chrome makes a scrollable container keyboard-focusable on its own,
          // so Tab landed on the menu itself even with every row at -1 (#1378).
          tabIndex={-1}
        >
          {matches.map((product, index) => {
            // Picking a definition is a buy decision in miniature, so the price
            // history rides along with the match instead of living only in the
            // Food Catalog (#697). Passive: it is already in the payload.
            const typical = typicalPriceOf(product);
            return (
              <button
                key={product.id}
                id={`${listId}-option-${index}`}
                type="button"
                role="option"
                // Out of the Tab order: focus stays on the cell input and the
                // rows are pointed at with aria-activedescendant, so Tab moves
                // on instead of walking the matches (#1378). Tab already
                // commits the highlighted match in onKeyDown.
                tabIndex={-1}
                aria-selected={index === activeIndex}
                className={index === activeIndex ? "placement-combobox-option active" : "placement-combobox-option"}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => pick(product)}
              >
                <span>
                  <strong>{product.name}</strong>
                  {product.storeSection || product.defaultUnit ? (
                    <small>{[product.storeSection, product.defaultUnit].filter(Boolean).join(" · ")}</small>
                  ) : null}
                  {typical ? <small className="typical-price-note" title={typicalPriceTitle(typical, t)}>{typicalPriceNote(typical, t)}</small> : null}
                </span>
              </button>
            );
          })}
          {showCreateAction ? (
            <button
              id={`${listId}-create`}
              type="button"
              role="option"
              tabIndex={-1}
              aria-selected={activeIndex === matches.length}
              className={activeIndex === matches.length ? "placement-combobox-add active" : "placement-combobox-add"}
              onMouseDown={(event) => event.preventDefault()}
              onClick={requestCreate}
            >
              <Plus size={14} /> {t("sheet.createNew", { name: value.trim() })}
            </button>
          ) : null}
        </div>
      ) : null}
    </span>
  );
}
